import { Typography } from '@mui/material'
import { useSelector } from 'react-redux'

import {
  selectTelematicsDeviceTransientStatuses,
  selectTelematicsTenantSourcesWithoutMobile,
} from '@/features/domain/account'
import {
  ReadonlyBlock,
  ReadonlyField,
  ReadonlyFieldLabel,
  ReadonlyFieldRow,
  ReadonlyLabel,
} from '@/forms-legacy'
import { useIsSimulation } from '@/hooks'
import { TELEMATICS_DUMMY_GPS_MOBILE_DEVICE_ID } from '@/server-data'

import { useTexts } from '../hooks/useTexts'

interface Props {
  unifiedVehicle: uui.domain.client.UnifiedVehicle
}

export function TelematicsTrackingSection(props: Props) {
  const { unifiedVehicle } = props

  const deviceTransientStatuses = useSelector(selectTelematicsDeviceTransientStatuses)
  const tenantSources = useSelector(selectTelematicsTenantSourcesWithoutMobile)
  const isSimulation = useIsSimulation()
  const texts = useTexts()

  // Retreive the device id
  const deviceId = unifiedVehicle.hasRoutingLicense
    ? (unifiedVehicle.vehicle.gpsDeviceId ?? '')
    : ''

  // Check if the vehicle has a device
  const hasDevice = !!deviceId

  // Retreive the physical device
  const device = unifiedVehicle.isPhysicalDevice
    ? (unifiedVehicle.device as uui.domain.client.gps.telematics.DeviceInfo)
    : undefined

  const deviceLabel = device
    ? device.tenantSourceId === 'TBD'
      ? texts.none
      : (device.deviceLabel ?? device.deviceId ?? '')
    : ''

  const tenantSource = device
    ? device.tenantSourceId === 'TBD'
      ? {
          id: 'TBD',
          label: texts.none,
          active: true,
          statusCode: 2000,
        }
      : tenantSources[device.tenantSourceId]
    : undefined

  const activationStatus = device
    ? unifiedVehicle.hasRoutingLicense
      ? (deviceTransientStatuses[device.deviceId]?.status ?? undefined)
      : undefined
    : undefined

  const activationStatusMessage =
    activationStatus === 'ERROR'
      ? texts.somethingWentWrongWithTheDevice
      : activationStatus === 'PENDING-ACTIVATION'
        ? texts.deviceActivationPending
        : undefined

  const activationStatusMessageColor =
    activationStatus === 'ERROR'
      ? 'error'
      : activationStatus === 'PENDING-ACTIVATION'
        ? 'primary'
        : undefined

  if (isSimulation) return null

  if (!deviceId) {
    return (
      <Typography
        variant="body2"
        fontWeight={600}
        data-testid="setup-vehicle__telematics-device-tracking-status"
        data-trackid="setup-vehicle__telematics-device-tracking-status"
      >
        {texts.vehicleNotTracked}
      </Typography>
    )
  }

  if (deviceId === TELEMATICS_DUMMY_GPS_MOBILE_DEVICE_ID) {
    return (
      <>
        <Typography
          variant="body2"
          fontWeight={600}
          mb={2}
          data-testid="setup-vehicle__telematics-device-tracking-status"
          data-trackid="setup-vehicle__telematics-device-tracking-status"
        >
          {texts.vehicleTracked}
        </Typography>

        <ReadonlyBlock
          data-testid="setup-vehicle__telematics-device-track-by"
          data-trackid="setup-vehicle__telematics-device-track-by"
        >
          <ReadonlyLabel>{texts.trackBy}</ReadonlyLabel>
          <ReadonlyField>
            <ReadonlyFieldRow
              data-testid="setup-vehicle__telematics-device-tracking-method"
              data-trackid="setup-vehicle__telematics-device-tracking-method"
            >
              <ReadonlyFieldLabel>{texts.driverApp}</ReadonlyFieldLabel>
            </ReadonlyFieldRow>
          </ReadonlyField>
        </ReadonlyBlock>
      </>
    )
  }

  if (!tenantSource) {
    console.error(`Tenant source not found for deviceId ${deviceId}`)
    return null
  }

  return (
    <>
      <Typography
        variant="body2"
        fontWeight={600}
        mb={2}
        data-testid="setup-vehicle__telematics-device-tracking-status"
        data-trackid="setup-vehicle__telematics-device-tracking-status"
      >
        {texts.vehicleTracked}
      </Typography>

      {tenantSource.statusCode === 2000 && !tenantSource.active ? (
        <ReadonlyBlock>
          <Typography
            color="primary"
            fontWeight={600}
            variant="caption"
            data-testid="setup-vehicle__telematics-device-activation-status"
          >
            {texts.integrationDeleteInProgress}
          </Typography>
        </ReadonlyBlock>
      ) : null}

      <ReadonlyBlock
        data-testid="setup-vehicle__telematics-device-track-by"
        data-trackid="setup-vehicle__telematics-device-track-by"
      >
        <ReadonlyLabel>{texts.trackBy}</ReadonlyLabel>
        <ReadonlyField>
          <ReadonlyFieldRow
            data-testid="setup-vehicle__telematics-device-tracking-method"
            data-trackid="setup-vehicle__telematics-device-tracking-method"
          >
            <ReadonlyFieldLabel>{tenantSource.label}</ReadonlyFieldLabel>
          </ReadonlyFieldRow>
        </ReadonlyField>
      </ReadonlyBlock>

      {hasDevice && (
        <ReadonlyBlock
          data-testid="setup-vehicle__telematics-device-label"
          data-trackid="setup-vehicle__telematics-device-label"
        >
          <ReadonlyLabel>{texts.assignedGpsDevice}</ReadonlyLabel>
          <ReadonlyField>
            <ReadonlyFieldRow>
              <ReadonlyFieldLabel>{deviceLabel}</ReadonlyFieldLabel>
            </ReadonlyFieldRow>
          </ReadonlyField>
        </ReadonlyBlock>
      )}

      {activationStatus && (
        <Typography
          color={activationStatusMessageColor}
          variant="caption"
          fontWeight={600}
          data-testid="setup-vehicle__telematics-device-activation-status"
        >
          {activationStatusMessage}
        </Typography>
      )}
    </>
  )
}
